import React, {useEffect, useMemo, useState} from "react";
import {useLocation} from "react-router-dom";
import {ChevronRight} from "lucide-react";
import {
    PAGE_TABS_EVENT,
    getHeaderTabs,
    getTabHref,
} from "./pageTabsStorage";
import {getTopMenuIcon} from "../../layouts/topMenuIcons";

function pathMatches(currentPath, tab) {
    const href = getTabHref(tab);
    if (!href) return false;
    return currentPath === href || currentPath.startsWith(`${href}/`);
}

/** 현재 경로가 매칭되는 헤더 탭과 (있다면) 자식 탭 반환 */
function findActiveCrumb(headerTabs, pathname) {
    for (let i = 0; i < headerTabs.length; i++) {
        const header = headerTabs[i];
        const childMatch = (header.childTabs ?? []).find((c) => pathMatches(pathname, c));
        if (childMatch) {
            return {header, child: childMatch};
        }
        if (pathMatches(pathname, header)) {
            return {header, child: null};
        }
    }
    return {header: null, child: null};
}

/**
 * 현재 페이지의 위치를 [헤더 탭] > [자식 탭] 형태로 보여주는 네비게이션 정보.
 * - PageTabs와 동일한 데이터 소스(localStorage 기반 headerTabs)를 사용
 * - 헤더/자식 탭 변경(PAGE_TABS_EVENT) 시 자동 갱신
 */
export default function PageBreadcrumb({className = ""}) {
    const [headerTabs, setHeaderTabs] = useState(() => getHeaderTabs());
    const location = useLocation();

    useEffect(() => {
        const handler = (e) => {
            setHeaderTabs(e.detail?.headerTabs ?? getHeaderTabs());
        };
        const storageHandler = (e) => {
            if (e.key === "pageTabs") setHeaderTabs(getHeaderTabs());
        };
        window.addEventListener(PAGE_TABS_EVENT, handler);
        window.addEventListener("storage", storageHandler);
        return () => {
            window.removeEventListener(PAGE_TABS_EVENT, handler);
            window.removeEventListener("storage", storageHandler);
        };
    }, []);

    const {header, child} = useMemo(
        () => findActiveCrumb(headerTabs, location.pathname),
        [headerTabs, location.pathname]
    );

    if (!header) return null;

    return (
        <nav
            className={`page-breadcrumb ${className}`.trim()}
            aria-label="현재 페이지 위치"
        >
            {header.icon && (
                <span className="page-breadcrumb-icon" aria-hidden="true">
                    {getTopMenuIcon(header.icon)}
                </span>
            )}
            <span className={child ? "page-breadcrumb-segment" : "page-breadcrumb-segment is-current"}>
                {header.title}
            </span>
            {child && (
                <>
                    <ChevronRight
                        size={12}
                        strokeWidth={2}
                        className="page-breadcrumb-sep"
                        aria-hidden="true"
                    />
                    <span className="page-breadcrumb-segment is-current">{child.title}</span>
                </>
            )}
        </nav>
    );
}
